/**
 * Motion-level hook - Wave 0 of the 2026-05-27 visibility-buildout.
 *
 * Tells components how much motion they should apply. The level
 * combines THREE signals:
 *   1. `prefers-reduced-motion` - OS-level accessibility preference
 *   2. Battery state - when the device is on battery + below 20% we
 *      downgrade to "reduced" automatically (saves ~5-8% battery life
 *      across a long SOC shift)
 *   3. `navigator.connection.saveData` - user opted into data saver,
 *      which on most browsers also implies "save battery"
 *
 * Levels:
 *   - "full":    spring physics, GPU transforms, blur transitions, aurora drift
 *   - "reduced": fade-only transitions, no parallax/refraction, no shine sweeps
 *   - "off":     no animation at all (transition-property: none)
 *
 * Usage:
 *   const level = useMotionLevel();
 *   <motion.div animate={level === 'off' ? false : { y: 0 }} ... />
 *
 * Implementation notes:
 *   - The Battery Status API is deprecated in Chromium (privacy) but
 *     still works on Safari/Firefox. When unavailable we fall back to
 *     prefers-reduced-motion alone.
 *   - The hook is SSR-safe: returns "full" during server render and
 *     hydrates client-side on mount.
 */

import { useEffect, useState } from 'react';

export type MotionLevel = 'full' | 'reduced' | 'off';

interface BatteryManager {
  charging: boolean;
  level: number;
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

interface NavigatorWithBattery {
  getBattery?(): Promise<BatteryManager>;
  connection?: { saveData?: boolean };
}

function computeLevel(
  prefersReduced: boolean,
  saveData: boolean,
  battery: BatteryManager | null,
): MotionLevel {
  if (prefersReduced) return 'off';
  if (saveData) return 'reduced';
  if (battery && !battery.charging && battery.level < 0.2) return 'reduced';
  return 'full';
}

export function useMotionLevel(): MotionLevel {
  const [level, setLevel] = useState<MotionLevel>('full');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const nav = window.navigator as unknown as NavigatorWithBattery;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');

    let battery: BatteryManager | null = null;
    let cancelled = false;

    function recompute(): void {
      if (cancelled) return;
      const prefersReduced = mq.matches;
      const saveData = nav.connection?.saveData === true;
      setLevel(computeLevel(prefersReduced, saveData, battery));
    }

    // Initial compute + listen to changes.
    recompute();
    mq.addEventListener('change', recompute);

    // Battery is async + best-effort.
    nav
      .getBattery?.()
      .then((b) => {
        if (cancelled) return;
        battery = b;
        b.addEventListener('chargingchange', recompute);
        b.addEventListener('levelchange', recompute);
        recompute();
      })
      .catch(() => {
        /* battery API blocked or unsupported - fall back to mq alone */
      });

    return () => {
      cancelled = true;
      mq.removeEventListener('change', recompute);
      if (battery) {
        battery.removeEventListener('chargingchange', recompute);
        battery.removeEventListener('levelchange', recompute);
      }
    };
  }, []);

  return level;
}

/**
 * Convenience: returns a motion-prop helper that disables animation
 * when level === 'off' and shortens duration when level === 'reduced'.
 *
 *   const { transition, fade } = useMotionPreset();
 *   <motion.div initial={fade.hidden} animate={fade.visible} transition={transition} />
 */
export function useMotionPreset() {
  const level = useMotionLevel();
  const transition =
    level === 'off'
      ? { duration: 0 }
      : level === 'reduced'
        ? { duration: 0.12, ease: [0.4, 0, 0.2, 1] as const }
        : { type: 'spring' as const, stiffness: 280, damping: 28 };

  return {
    level,
    transition,
    fade: {
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
    },
    rise: {
      hidden: level === 'full' ? { opacity: 0, y: 8 } : { opacity: 0 },
      visible: { opacity: 1, y: 0 },
    },
    slideRight: {
      hidden: level === 'full' ? { opacity: 0, x: -8 } : { opacity: 0 },
      visible: { opacity: 1, x: 0 },
    },
  };
}
